// src/pages/emailMarketing/ContactsListPage.jsx
import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import emailMarketingService from '../../helpers/emailMarketingService';
import { FaUsers, FaSearch } from 'react-icons/fa';

function ContactsListPage() {
    const { user } = useAuthStore();
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({});

    useEffect(() => {
        fetchMarcas();
    }, [user]);

    useEffect(() => {
        if (selectedMarca) {
            fetchContacts();
        }
    }, [selectedMarca, currentPage, searchTerm]);

    const fetchMarcas = async () => {
        try {
            if (user?.userId) {
                const response = await get(`marcas/user/${user.userId}`);
                setMarcas(response);
                if (response.length > 0) {
                    setSelectedMarca(response[0]._id);
                }
            }
        } catch (error) {
            // Error silencioso
        }
    };

    const fetchContacts = async () => {
        setLoading(true);
        try {
            const filters = {
                page: currentPage,
                limit: 50,
                acceptsMarketing: true
            };
            if (searchTerm) {
                filters.search = searchTerm;
            }

            const response = await emailMarketingService.getContacts(selectedMarca, filters);
            const contactsData = response.data || response || [];
            
            setContacts(contactsData);
            setPagination(response.pagination || {});
        } catch (error) {
            // Error silencioso
        } finally {
            setLoading(false);
        }
    };

    if (loading && contacts.length === 0) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Contactos de Email Marketing</h1>
                {marcas.length > 0 && (
                    <select
                        value={selectedMarca || ''}
                        onChange={(e) => setSelectedMarca(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded"
                    >
                        {marcas.map(marca => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por email o nombre..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded"
                    />
                </div>
            </div>

            {/* Listado */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {contacts.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No hay contactos disponibles.
                    </div>
                ) : (
                    <>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consentimiento</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {contacts.map((contact) => (
                                    <tr key={contact._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{contact.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {contact.name || contact.nombre || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {contact.emailMarketingTags?.map((tag, idx) => (
                                                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                contact.acceptsMarketing 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {contact.acceptsMarketing ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {contact.suppressed ? (
                                                <div className="flex flex-col">
                                                    <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 mb-1">
                                                        Suprimido
                                                    </span>
                                                    {contact.suppressionReason && (
                                                        <span className="text-xs text-gray-500 mt-1" title={`Suprimido el ${contact.suppressedAt ? new Date(contact.suppressedAt).toLocaleDateString() : ''}`}>
                                                            {contact.suppressionReason === 'hard_bounce' ? 'Bounce permanente' : 
                                                             contact.suppressionReason === 'spam_complaint' ? 'Marcado como spam' : 
                                                             contact.suppressionReason === 'manual' ? 'Manual' : 
                                                             contact.suppressionReason}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                                                    Activo
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {pagination.totalPages > 1 && (
                            <div className="px-6 py-4 flex justify-between items-center">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 border rounded disabled:opacity-50"
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-600">
                                    Página {currentPage} de {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                                    disabled={currentPage === pagination.totalPages}
                                    className="px-4 py-2 border rounded disabled:opacity-50"
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default ContactsListPage;

